import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { auth } from '../services/auth';
import { analysisApi } from '../services/analysis';
import { billingApi } from '../services/billing';
import { Screen } from '../components/lease/Screen';
import { BrandHeading } from '../components/lease/BrandHeading';
import { PrimaryButton } from '../components/lease/PrimaryButton';
import { RiskReport } from '../features/lease/RiskReport';
import { ProfileSettings } from '../features/dashboard/ProfileSettings';

const STATUS_STYLES = {
  completed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300',
  processing: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300',
  pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300',
  failed: 'bg-rose-100 text-rose-700 dark:bg-rose-900/50 dark:text-rose-300',
};

function StatusPill({ status }) {
  const style = STATUS_STYLES[status] || STATUS_STYLES.pending;
  return (
    <span className={`inline-block flex-none rounded-full px-2.5 py-0.5 text-xs font-semibold ${style}`}>
      {status}
    </span>
  );
}

function ReviewCard({ doc, onView, onDelete }) {
  const date = new Date(doc.created_at).toLocaleDateString();
  const [confirming, setConfirming] = useState(false);
  return (
    <div className="rounded-2xl bg-white/70 p-4 shadow-sm ring-1 ring-emerald-100 backdrop-blur dark:bg-slate-800/60 dark:ring-slate-700">
      <div className="flex items-start justify-between gap-2">
        <p className="min-w-0 truncate font-semibold text-slate-800 dark:text-slate-100">{doc.filename}</p>
        <StatusPill status={doc.analysis_status} />
      </div>
      <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{date}</p>
      {doc.file_purged_at && (
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          Original PDF deleted on {new Date(doc.file_purged_at).toLocaleDateString()} per
          our retention policy — your report is unaffected.
        </p>
      )}
      {doc.analysis_status === 'completed' && (
        <button
          onClick={() => onView(doc.id)}
          className="mt-3 w-full rounded-xl bg-white py-2 text-sm font-bold text-emerald-700 ring-1 ring-emerald-200 transition hover:bg-emerald-50 dark:bg-slate-800/60 dark:text-emerald-400 dark:ring-slate-700"
        >
          View report
        </button>
      )}
      {doc.analysis_status !== 'processing' &&
        (!confirming ? (
          <button
            onClick={() => setConfirming(true)}
            className="mt-2 w-full text-xs font-semibold text-rose-600 underline dark:text-rose-400"
          >
            Delete this lease
          </button>
        ) : (
          <div className="mt-2 flex items-center justify-center gap-3 text-xs">
            <span className="text-slate-700 dark:text-slate-200">
              Delete this lease and its report permanently?
            </span>
            <button
              onClick={() => onDelete(doc.id)}
              className="font-bold text-rose-600 underline dark:text-rose-400"
            >
              Yes, delete
            </button>
            <button
              onClick={() => setConfirming(false)}
              className="font-semibold text-slate-600 underline dark:text-slate-300"
            >
              Cancel
            </button>
          </div>
        ))}
    </div>
  );
}

export function Dashboard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [viewingId, setViewingId] = useState(null);

  const deleteMutation = useMutation({
    mutationFn: (id) => analysisApi.deleteDocument(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['documents'] }),
  });

  const { data: me } = useQuery({ queryKey: ['me'], queryFn: auth.me, retry: false });
  // suspense:false (overriding the app-wide default) so a failed/empty list
  // degrades to a graceful empty state instead of throwing to the ErrorBoundary.
  const { data: documents } = useQuery({
    queryKey: ['documents'],
    queryFn: analysisApi.getDocuments,
    retry: false,
    suspense: false,
  });
  // Only fetch a specific doc once the user opens one — never with a null id
  // (which would hit /pdf/documents/null and 422 on the int-parse).
  const { data: viewingDoc } = useQuery({
    queryKey: ['document', viewingId],
    queryFn: () => analysisApi.getDocument(viewingId),
    enabled: !!viewingId,
    retry: false,
    suspense: false,
  });

  if (viewingDoc?.analysis_results) {
    return (
      <RiskReport
        report={viewingDoc.analysis_results}
        details={viewingDoc.lease_details}
        onDownload={() => analysisApi.downloadReport(viewingId)}
        onWriteRequest={({ issues, checked }) =>
          navigate('/toolkit', {
            state: { prefill: { issues, checked, documentId: viewingId } },
          })
        }
      />
    );
  }

  return (
    <Screen>
      <header className="animate-fade-in text-center">
        <BrandHeading className="text-4xl">
          Welcome{me?.full_name ? `, ${me.full_name}` : ''}
        </BrandHeading>
        {me && (
          <p className="mt-3 text-slate-600 dark:text-slate-300">
            {me.plan_tier && (
              <>
                <span className="font-semibold text-emerald-700 dark:text-emerald-400 capitalize">
                  {me.plan_tier} Plan
                </span>
                {' — '}
              </>
            )}
            <span className="font-semibold text-emerald-700 dark:text-emerald-400">
              {me.credit_balance} credit{me.credit_balance === 1 ? '' : 's'}
            </span>{' '}
            remaining
            {me.credits_expire_at && (
              <> until {new Date(me.credits_expire_at).toLocaleDateString()}</>
            )}
          </p>
        )}
        {me &&
          ['standard', 'premium'].includes(me.plan_tier) &&
          me.credit_balance > 0 &&
          me.credits_expire_at &&
          new Date(me.credits_expire_at) > new Date() && (
            <button
              onClick={() =>
                billingApi
                  .checkout('extension')
                  .then(({ checkout_url }) => window.location.assign(checkout_url))
              }
              className="mt-3 text-sm font-semibold text-emerald-700 underline dark:text-emerald-400"
            >
              Extend 30 days — $9.95
            </button>
          )}
      </header>

      <PrimaryButton onClick={() => navigate('/lease-analysis')} className="mt-8 animate-slide-in">
        Start a new review
      </PrimaryButton>

      <section className="mt-10 animate-slide-in">
        <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100">Your reviews</h2>
        <div className="mt-4 space-y-3">
          {documents && documents.length > 0 ? (
            documents.map((doc) => (
              <ReviewCard
                key={doc.id}
                doc={doc}
                onView={setViewingId}
                onDelete={(id) => deleteMutation.mutate(id)}
              />
            ))
          ) : (
            <div className="rounded-2xl bg-white/70 p-4 text-center shadow-sm ring-1 ring-emerald-100 backdrop-blur dark:bg-slate-800/60 dark:ring-slate-700">
              <p className="text-slate-600 dark:text-slate-300">
                No reviews yet — upload your first lease.
              </p>
            </div>
          )}
        </div>
      </section>

      {me && (
        <section className="mt-10 animate-slide-in rounded-2xl bg-white/70 p-4 shadow-sm ring-1 ring-emerald-100 backdrop-blur dark:bg-slate-800/60 dark:ring-slate-700">
          <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Account</h2>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">{me.email}</p>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
            Member since {new Date(me.created_at).toLocaleDateString()}
          </p>
          <ProfileSettings />
        </section>
      )}
    </Screen>
  );
}

export default Dashboard;
